import { useAppDispatch } from "@app/hooks";
import Switch from "@components/Switch";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldTooltip } from "@emerald-react/tooltip";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import { useSortableItem } from "@hooks/useSortable";
import {
  useDeletePropertyMutation,
  useTogglePropertyVisibilityMutation,
} from "@services/survey-properties";
import Analytics from "@shared/utils/tagManagerAnalytics";
import { PropertyWithOrder } from "@survey-types/survey.property.types";
import {
  currentPropertyId as currentPropertyIdAction,
  removeProperty,
} from "@survey/surveySlice";
import classNames from "classnames";

interface PropertyListItemProps {
  property: PropertyWithOrder;
  index: number;
  isDraggable?: boolean;
  isSelected?: boolean;
  isSelectionDisabled?: boolean;
  selectionDisabledMessage?: string;
  onToggleSelect?: (id: string, selected: boolean) => void;
}

const PropertyListItem = ({
  property,
  index,
  isDraggable = true,
  isSelected = false,
  isSelectionDisabled = false,
  selectionDisabledMessage,
  onToggleSelect,
}: PropertyListItemProps) => {
  const dispatch = useAppDispatch();
  const { attributes, listeners, setNodeRef, style, isDragging } =
    useSortableItem(property.id);
  const [togglePropertyVisibility] = useTogglePropertyVisibilityMutation();
  const [deleteProperty] = useDeletePropertyMutation();
  const { quickDelete, withLoading } = useSimpleConfirmation();

  // Check if property has invalid coordinates
  const hasInvalidCoordinates = () => {
    const lat = parseFloat(property.latitude);
    const lng = parseFloat(property.longitude);

    const isLatInvalid =
      isNaN(lat) ||
      lat < -90 ||
      lat > 90 ||
      property.latitude.trim() === "" ||
      lat === 0;
    const isLngInvalid =
      isNaN(lng) ||
      lng < -180 ||
      lng > 180 ||
      property.longitude.trim() === "" ||
      lng === 0;

    return isLatInvalid || isLngInvalid;
  };

  const handleToggleVisibility = () => {
    togglePropertyVisibility({
      propertyId: property.id,
      isHidden: !property.isHidden,
    });
  };

  const handleDelete = () => {
    quickDelete({
      title: "Property",
      text: `This will remove the Property and all of its information from the survey.
            Are you sure you want to delete this Property?`,
      id: "SC-ConfirmDeleteProperty",
    })
      .then((confirmed) => {
        if (confirmed) {
          dispatch(removeProperty(property.id));
          withLoading(
            () =>
              deleteProperty({
                surveyId: property.surveyId,
                propertyId: property.id,
              }).unwrap(),
            {
              loadingText: "Deleting Property...",
              successText: "Property deleted successfully!",
              errorText: "Failed to delete Property.",
            },
          ).catch((_error) => {
            console.error("Failed to delete property");
          });
        }
      })
      .catch((_error) => {
        console.error("Error in confirmation dialog");
      });
  };

  const handleSelectProperty = () => {
    Analytics.send({
      DragHandleEntity: "Individual Property",
    });
    dispatch(currentPropertyIdAction({ propertyId: property.id }));
  };

  const selectionCheckbox = (
    <input
      type="checkbox"
      checked={isSelected}
      disabled={isSelectionDisabled}
      onChange={(e) => onToggleSelect?.(property.id, e.target.checked)}
      className={classNames({
        "cursor-pointer": !isSelectionDisabled,
        "cursor-not-allowed opacity-50": isSelectionDisabled,
      })}
      style={{ width: '16px', height: '16px', accentColor: '#003f2d', flexShrink: 0 }}
    />
  );

  return (
    <div
      id="SC-DragHandle"
      ref={setNodeRef}
      style={style}
      className={classNames(
        "flex items-center gap-4 p-4 bg-primary-lighter hover:bg-divider-2 transition-colors group",
        {
          "opacity-50 bg-slate-100": isDragging,
        },
      )}
    >
      {/* Drag Handle */}
      <div
        className={classNames("flex items-center text-slate-400", {
          "cursor-move": isDraggable,
          "cursor-default opacity-30": !isDraggable,
        })}
        {...(isDraggable ? attributes : {})}
        {...(isDraggable ? listeners : {})}
      >
        <EmeraldIcon
          icon="drag_indicator"
          className="text-gray-400 hover:text-gray-600 transition-colors"
        />
      </div>
             {/* Checkbox — native input avoids MDCCheckbox stale foundation state */}
      {onToggleSelect &&
        (isSelectionDisabled ? (
          <EmeraldTooltip
            trigger="hover"
            placement="bottom"
            delayShow={0}
            delayHide={100}
            followCursor={false}
            interactive={true}
            closeOnOutsideClick={true}
            triggerElement={
              <span className="inline-flex cursor-not-allowed">
                {selectionCheckbox}
              </span>
            }
          >
            <div className="max-w-xs text-xs leading-5">
              {selectionDisabledMessage ||
                "Point styling is not enabled for this property."}
            </div>
          </EmeraldTooltip>
        ) : (
          selectionCheckbox
        ))}

      {/* Order Number */}
      <div className="w-6 text-sm font-medium text-slate-600 flex items-center justify-center">
        {property.isCurrentLocation ? (
          <EmeraldIcon
            icon="star"
            type="filled"
            fontsizeInRem={1}
            variant="icon"
          />
        ) : property.isHidden ? (
          "–"
        ) : (
          index + 1
        )}
      </div>
      <div
        className="flex-1 hover:cursor-pointer hover:underline flex gap-x-3"
        onClick={handleSelectProperty}
      >
        {hasInvalidCoordinates() && (
          <div title="Invalid Coordinates">
            <EmeraldIcon
              icon="warning_amber"
              type="filled"
              fontsizeInRem={1.2}
              className="text-orange-600"
              variant="icon"
            />
          </div>
        )}
        <div className="flex-col flex">
          <div className="truncate text-base font-normal text-primary">
            {property.name}
          </div>
          <div className="text-base text-primary">{property.address1}</div>
        </div>
      </div>
      {/* actions */}
      <div className="flex items-center gap-x-10">
        {/* Shortlisted Status */}
        <div className="flex items-center gap-2 min-w-25">
          {property.isClientShortlist && (
            <>
              <EmeraldIcon
                icon="check"
                type="filled"
                fontsizeInRem={1.2}
                className="text-green-600"
                variant="icon"
              />
              <span className="text-sm text-slate-600">Shortlisted</span>
            </>
          )}
        </div>
      </div>
      <div className="flex w-52 items-center gap-x-10">
        <Switch
          checked={property.isVisible}
          onChange={handleToggleVisibility}
          size="small"
          label="Show"
          labelPosition="left"
        />

        {/* Delete Button */}
        <EmeraldIconButton
          icon="delete"
          type="Icon"
          className="text-data-light transition-all"
          onClick={handleDelete}
          id="SC-DeleteProperty"
        />
      </div>
    </div>
  );
};

export default PropertyListItem;
